import { BALANCE, type EmailError, type UsernameError } from '@2k48/shared';
import { ApiError } from '../net/client';
import { i18n } from './i18n/index.svelte';

/**
 * Wording for the account screens.
 *
 * The server answers with stable codes and never with prose, so every sentence
 * a player reads is decided on this side. That was the whole point of the
 * arrangement, and this is the file it was made for: adding a language changed
 * the catalogue, not a single endpoint.
 *
 * Resolved eagerly, at the moment the request fails. Switching language while
 * an error is already on screen leaves that one sentence in the old language
 * until the next attempt — a corner worth naming, and not worth threading an
 * error code through every form to avoid.
 */

type ErrorKey = keyof ReturnType<typeof errors>;

function errors() {
  return i18n.t.auth.errors;
}

const BY_CODE: Record<string, ErrorKey> = {
  invalid_credentials: 'invalidCredentials',
  account_disabled: 'accountDisabled',
  username_taken: 'usernameTaken',
  email_taken: 'emailTaken',
  player_already_claimed: 'playerAlreadyClaimed',
  invalid_username: 'invalidUsername',
  invalid_email: 'invalidEmail',
  device_required: 'deviceRequired',
  unauthorized: 'unauthorized',
};

function waitMessage(seconds: number): string {
  const minutes = Math.ceil(seconds / 60);
  return minutes <= 1 ? errors().rateLimitedOneMinute : errors().rateLimited(minutes);
}

/**
 * Turn a failed request into one sentence.
 *
 * A network failure and a rejected password are different problems and must not
 * share a message: one is "try again", the other is "you typed it wrong", and
 * telling someone their password is wrong when the server is unreachable sends
 * them to reset a password that was fine.
 */
export function authErrorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) return errors().offline;

  if (error.code === 'rate_limited') return waitMessage(error.retryAfter ?? 60);
  if (error.code === 'invalid_password') {
    return errors().invalidPassword(BALANCE.account.passwordMinLength);
  }

  const key = error.code ? BY_CODE[error.code] : undefined;
  if (key) {
    const message = errors()[key];
    // Every mapped key is a plain string; the two parameterised ones are
    // handled above. This keeps the lookup total without a cast.
    if (typeof message === 'string') return message;
  }

  return error.status >= 500 ? errors().serverError : errors().generic;
}

/** The input the server blamed, so the form can mark it. */
export function authErrorField(error: unknown): string | null {
  return error instanceof ApiError ? error.field : null;
}

const { usernameMinLength, usernameMaxLength } = BALANCE.account;

/** Same verdicts as the server's, phrased for someone still typing. */
export function usernameMessage(error: UsernameError): string {
  const copy = errors();
  switch (error) {
    case 'required':
      return copy.usernameRequired;
    case 'too_short':
      return copy.usernameTooShort(usernameMinLength);
    case 'too_long':
      return copy.usernameTooLong(usernameMaxLength);
    case 'charset':
      return copy.usernameCharset;
    case 'edges':
      return copy.usernameEdges;
  }
}

export function emailMessage(error: EmailError): string {
  const copy = errors();
  switch (error) {
    case 'required':
      return copy.emailRequired;
    case 'too_long':
      return copy.emailTooLong;
    case 'malformed':
      return copy.invalidEmail;
  }
}
